import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getCourse, updateCourse, uploadThumbnail, uploadEbook } from '../../services/api';
import { toast } from 'react-toastify';
import { FaBook, FaBookOpen, FaImage, FaFilePdf, FaSpinner, FaPlus, FaTrash, FaGripVertical } from 'react-icons/fa';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import ModuleBuilder from '../../components/instructor/ModuleBuilder.jsx';
import CourseStructurePreview from '../../components/instructor/CourseStructurePreview.jsx';
import ChapterManager from '../../components/ChapterManager.jsx';
import EbookStructurePreview from '../../components/instructor/EbookStructurePreview.jsx';
import './CreateCourse.css';

const EditCourse = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [fetchingCourse, setFetchingCourse] = useState(true);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [ebookFile, setEbookFile] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [modules, setModules] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    shortDescription: '',
    category: '',
    courseType: 'course',
    videoSubcategory: '',
    level: '',
    ebookSubcategory: '',
    indicatorName: '',
    price: 0,
    tags: '',
    requirements: '',
    learningObjectives: ''
  });

  // Load course data on mount
  useEffect(() => {
    const loadCourse = async () => {
      try {
        setFetchingCourse(true);
        const response = await getCourse(id);
        const course = response.data.course;

        // Populate form data
        setFormData({
          title: course.title || '',
          description: course.description || '',
          shortDescription: course.shortDescription || '',
          category: course.category || '',
          courseType: course.courseType || 'course',
          videoSubcategory: course.videoSubcategory || '',
          level: course.level || '',
          ebookSubcategory: course.ebookSubcategory || '',
          indicatorName: course.indicatorName || '',
          price: course.price || 0,
          tags: course.tags ? course.tags.join(', ') : '',
          requirements: course.requirements ? course.requirements.join('\n') : '',
          learningObjectives: course.learningObjectives ? course.learningObjectives.join('\n') : ''
        });

        // Set existing thumbnail preview
        if (course.thumbnail) {
          setThumbnailPreview(course.thumbnail);
        }

        // Set chapters if ebook has multi-chapter support
        if (course.chapters && Array.isArray(course.chapters)) {
          setChapters(course.chapters);
        }

        // Set modules if video course
        if (course.modules && Array.isArray(course.modules)) {
          setModules(course.modules.map(m => ({
            ...m,
            id: m._id || m.id || `module-${Date.now()}-${Math.random()}`, // Ensure ID exists for dnd
            lessons: m.lessons ? m.lessons.map(l => ({
              ...l,
              id: l._id || l.id || `lesson-${Date.now()}-${Math.random()}`
            })) : [],
            // Crucial Fix: Map backend subModules to frontend submodules state
            submodules: m.subModules ? m.subModules.map(s => ({
              ...s,
              id: s._id || s.id || `submodule-${Date.now()}-${Math.random()}`,
              lessons: s.lessons ? s.lessons.map(sl => ({
                ...sl,
                id: sl._id || sl.id || `lesson-${Date.now()}-${Math.random()}`
              })) : []
            })) : []
          })));
        }

      } catch (error) {
        console.error('Error loading course:', error);
        toast.error(error.response?.data?.message || 'Failed to load course');
        navigate('/instructor/courses');
      } finally {
        setFetchingCourse(false);
      }
    };

    loadCourse();
  }, [id, navigate]);

  const handleChaptersUpdate = async () => {
    // Refresh course data to get updated chapters
    try {
      const response = await getCourse(id);
      const course = response.data.course;
      if (course.chapters && Array.isArray(course.chapters)) {
        // Force new array reference to ensure React re-renders
        setChapters([...course.chapters]);
      } else {
        setChapters([]);
      }
    } catch (error) {
      console.error('Error refreshing chapters:', error);
      toast.error('Failed to refresh chapters list');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // When category changes to ebook, set courseType to ebook
    if (name === 'category') {
      if (value === 'ebook') {
        setFormData({
          ...formData,
          category: value,
          courseType: 'ebook',
          videoSubcategory: '',
          ebookSubcategory: formData.ebookSubcategory || ''
        });
        setModules([]); // Clear modules if switching to ebook
      } else {
        // If current type is ebook, keep it as ebook unless switching to a video-only category
        // But SISTEM and Tradingview are now allowed for both.
        const videoOnlyCategories = ['general', 'basics', 'technical-analysis', 'fundamental-analysis', 'risk-management', 'trading-psychology', 'strategies', 'advanced'];
        const isVideoCategory = videoOnlyCategories.includes(value);

        // If we are currently ebook, stay ebook unless it's a video-only category
        // If we are currently video, stay video
        let newType = formData.courseType;
        if (value === 'ebook') {
          newType = 'ebook';
        } else if (isVideoCategory) {
          newType = 'video';
        }
        // If category is 'sistem', we don't change the type (allows both ebook and video)

        setFormData({
          ...formData,
          category: value,
          courseType: newType,
          ebookSubcategory: ''
        });
        setChapters([]); // Clear chapters if switching away from ebook
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      setThumbnailFile(file);
      setThumbnailPreview(URL.createObjectURL(file));
    }
  };

  const handleEbookChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        toast.error('Please select a PDF file');
        return;
      }
      setEbookFile(file);
    }
  };

  // Module Management Handlers
  const handleAddModule = () => {
    const newModule = {
      id: `module-${Date.now()}-${Math.random()}`,
      title: '',
      submodules: [],
      lessons: [],
      order: modules.length
    };
    setModules([...modules, newModule]);
  };

  const handleUpdateModule = (moduleId, updatedModule) => {
    setModules(modules.map(m => (m.id === moduleId ? updatedModule : m)));
  };

  const handleDeleteModule = (moduleId) => {
    setModules(modules.filter(m => m.id !== moduleId));
  };

  const handleModuleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(modules);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update order property
    const reorderedModules = items.map((module, index) => ({
      ...module,
      order: index
    }));

    setModules(reorderedModules);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.title.trim()) {
      toast.error('Please enter a course title');
      return;
    }

    if (!formData.description.trim()) {
      toast.error('Please enter a course description');
      return;
    }

    if (!formData.category) {
      toast.error('Please select a category');
      return;
    }

    if (!formData.level) {
      toast.error('Please select a difficulty level');
      return;
    }

    // Ebook-specific validation (Only if category is also ebook)
    if (formData.courseType === 'ebook' && formData.category === 'ebook' && !formData.ebookSubcategory) {
      toast.error('Please select an ebook level');
      return;
    }

    // Video-specific validation (Handle both 'video' and legacy 'course')
    if (formData.courseType === 'video' || formData.courseType === 'course') {
      if (modules.length === 0) {
        toast.error('Please add at least one module for the video course');
        return;
      }
      // Validate modules have content
      const hasContent = modules.some(m =>
        (m.lessons && m.lessons.length > 0) ||
        (m.submodules && m.submodules.length > 0)
      );
      if (!hasContent) {
        toast.error('Please add lessons to your modules');
        return;
      }
    }

    setLoading(true);

    try {
      // Prepare course data
      const courseData = {
        title: formData.title,
        description: formData.description,
        shortDescription: formData.shortDescription,
        category: formData.category,
        courseType: formData.courseType === 'course' ? 'video' : formData.courseType, // Auto-migrate legacy 'course' to 'video'
        level: formData.level,
        price: parseFloat(formData.price) || 0,
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        requirements: formData.requirements.split('\n').filter(req => req.trim()),
        learningObjectives: formData.learningObjectives.split('\n').filter(obj => obj.trim())
      };

      // Add ebook-specific data (Only for Ebook Category)
      if (formData.courseType === 'ebook' && formData.category === 'ebook') {
        courseData.ebookSubcategory = formData.ebookSubcategory;
      }

      // Add video subcategory for TradingView (Works for Ebook too)
      if (formData.category === 'tradingview') {
        courseData.videoSubcategory = formData.videoSubcategory;
      }

      // Add SISTEM subcategory (Video AND Ebook)
      if (formData.category === 'sistem') {
        courseData.videoSubcategory = formData.videoSubcategory;
        if (formData.videoSubcategory === 'indicator') {
          courseData.indicatorName = formData.indicatorName;
        }
      }

      // Add Modules (Crucial Fix: Send modules in payload for both 'video' and legacy 'course')
      if (formData.courseType === 'video' || formData.courseType === 'course') {
        courseData.modules = modules.map((m, idx) => ({
          ...m,
          order: idx, // Ensure order is correct
          lessons: m.lessons.map((l, lIdx) => ({
            ...l,
            order: lIdx,
            type: l.type || 'video' // Ensure type is set for validation
          })),
          // Crucial Fix: Map submodules and their nested lessons
          subModules: m.submodules ? m.submodules.map((s, sIdx) => ({
            ...s,
            order: sIdx,
            lessons: s.lessons ? s.lessons.map((sl, slIdx) => ({
              ...sl,
              order: slIdx,
              type: sl.type || 'video' // Ensure type is set for nested lessons
            })) : []
          })) : []
        }));
      }

      // Update course
      await updateCourse(id, courseData);

      // Upload new thumbnail if provided
      if (thumbnailFile) {
        const thumbnailFormData = new FormData();
        thumbnailFormData.append('thumbnail', thumbnailFile);
        await uploadThumbnail(id, thumbnailFormData);
      }

      // Upload new ebook PDF if provided
      if (formData.courseType === 'ebook' && ebookFile) {
        const ebookFormData = new FormData();
        ebookFormData.append('ebook', ebookFile);
        await uploadEbook(id, ebookFormData);
      }

      toast.success(
        formData.courseType === 'ebook'
          ? 'Ebook updated successfully!'
          : 'Course updated successfully!'
      );

      navigate(`/instructor/courses`);
    } catch (error) {
      console.error('Error updating course:', error);
      toast.error(error.response?.data?.message || 'Failed to update course');
    } finally {
      setLoading(false);
    }
  };

  const isEbook = formData.courseType === 'ebook';
  const isVideo = formData.courseType === 'video' || formData.courseType === 'course'; // Backward compatibility

  if (fetchingCourse) {
    return (
      <div className="create-course-page">
        <div className="container">
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
            flexDirection: 'column',
            gap: '1rem'
          }}>
            <FaSpinner className="spinner" size={48} />
            <p style={{ color: 'var(--text-secondary)' }}>Loading course...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="create-course-page">
      <div className="container">
        <div className="create-course-header">
          <h1>
            {isEbook ? <FaBookOpen /> : <FaBook />}
            {isEbook ? 'Edit Ebook' : 'Edit Course'}
          </h1>
          <p>Update the details for your {isEbook ? 'ebook' : 'course'}</p>
        </div>

        <form onSubmit={handleSubmit} className="create-course-form">
          <div className="form-section">
            <h2>Basic Information</h2>

            <div className="form-group">
              <label htmlFor="title">Title *</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Enter course title"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="shortDescription">Short Description</label>
              <textarea
                id="shortDescription"
                name="shortDescription"
                value={formData.shortDescription}
                onChange={handleInputChange}
                placeholder="Brief description for course cards (max 300 characters)"
                maxLength={300}
                rows={2}
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Full Description *</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Detailed course description"
                rows={6}
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="category">Category *</label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Category</option>
                  <option value="tradingview">Tradingview</option>
                  <option value="indicator">Indicator</option>
                  <option value="sistem">SISTEM</option>
                  <option value="general">General</option>
                  <option value="basics">Basics</option>
                  <option value="technical-analysis">Technical Analysis</option>
                  <option value="fundamental-analysis">Fundamental Analysis</option>
                  <option value="risk-management">Risk Management</option>
                  <option value="trading-psychology">Trading Psychology</option>
                  <option value="strategies">Strategies</option>
                  <option value="advanced">Advanced</option>
                  <option value="ebook">Ebook</option>
                </select>
              </div>

              {isEbook && formData.category === 'ebook' && (
                <div className="form-group">
                  <label htmlFor="ebookSubcategory">Ebook Level *</label>
                  <select
                    id="ebookSubcategory"
                    name="ebookSubcategory"
                    value={formData.ebookSubcategory}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Ebook Level</option>
                    <option value="basic">Basic</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </div>
              )}

              {formData.category === 'tradingview' && (
                <div className="form-group">
                  <label htmlFor="videoSubcategory">Subcategory *</label>
                  <select
                    id="videoSubcategory"
                    name="videoSubcategory"
                    value={formData.videoSubcategory}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Subcategory</option>
                    <option value="handphone">Modul Handphone</option>
                    <option value="laptop">Modul Laptop</option>
                  </select>
                </div>
              )}

              {formData.category === 'sistem' && (
                <div className="form-group">
                  <label htmlFor="videoSubcategory">SISTEM Subcategory *</label>
                  <select
                    id="videoSubcategory"
                    name="videoSubcategory"
                    value={formData.videoSubcategory}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Subcategory</option>
                    <option value="indicator">Indicator</option>
                    <option value="ea-copier">EA Copier</option>
                  </select>
                </div>
              )}

              {formData.category === 'sistem' && formData.videoSubcategory === 'indicator' && (
                <div className="form-group">
                  <label htmlFor="indicatorName">Indicator Name *</label>
                  <input
                    type="text"
                    id="indicatorName"
                    name="indicatorName"
                    value={formData.indicatorName || ''}
                    onChange={handleInputChange}
                    placeholder="e.g., SFT, OMS, STE, GOOGEM"
                    required
                  />
                  <small style={{ display: 'block', marginTop: '0.5rem', color: 'var(--text-secondary)' }}>
                    Nama indicator untuk display dalam SISTEM page
                  </small>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="level">Difficulty Level *</label>
                <select
                  id="level"
                  name="level"
                  value={formData.level}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Level</option>
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="price">Price ($)</label>
                <input
                  type="number"
                  id="price"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="tags">Tags (comma-separated)</label>
              <input
                type="text"
                id="tags"
                name="tags"
                value={formData.tags}
                onChange={handleInputChange}
                placeholder="e.g., forex, stocks, day trading"
              />
            </div>
          </div>

          <div className="form-section">
            <h2>Media</h2>

            <div className="form-group">
              <label htmlFor="thumbnail">
                <FaImage /> Thumbnail Image
              </label>
              <div className="file-upload-area">
                {thumbnailPreview ? (
                  <div className="image-preview">
                    <img src={thumbnailPreview} alt="Thumbnail preview" />
                    <button
                      type="button"
                      className="btn-remove"
                      onClick={() => {
                        setThumbnailFile(null);
                        setThumbnailPreview(null);
                      }}
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <label htmlFor="thumbnail" className="file-upload-label">
                    <FaImage size={48} />
                    <span>Click to upload thumbnail</span>
                    <small>PNG, JPG, GIF up to 10MB</small>
                  </label>
                )}
                <input
                  type="file"
                  id="thumbnail"
                  accept="image/*"
                  onChange={handleThumbnailChange}
                  style={{ display: 'none' }}
                />
              </div>
            </div>

            {isEbook && (
              <>
                <div className="form-group">
                  <label htmlFor="ebookPdf">
                    <FaFilePdf /> Single Ebook PDF File (Legacy)
                  </label>
                  <div className="file-upload-area">
                    {ebookFile ? (
                      <div className="file-selected">
                        <FaFilePdf size={48} color="#dc2626" />
                        <span>{ebookFile.name}</span>
                        <button
                          type="button"
                          className="btn-remove"
                          onClick={() => setEbookFile(null)}
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <label htmlFor="ebookPdf" className="file-upload-label">
                        <FaFilePdf size={48} />
                        <span>Click to upload new PDF (optional)</span>
                        <small>PDF file up to 10MB - For single-file ebooks only</small>
                      </label>
                    )}
                    <input
                      type="file"
                      id="ebookPdf"
                      accept="application/pdf"
                      onChange={handleEbookChange}
                      style={{ display: 'none' }}
                    />
                  </div>
                  <small style={{ display: 'block', marginTop: '0.5rem', color: 'var(--text-secondary)' }}>
                    For multi-chapter ebooks, use the Chapter Manager below instead.
                  </small>
                </div>

                <ChapterManager
                  courseId={id}
                  chapters={chapters}
                  onChaptersUpdate={handleChaptersUpdate}
                />

                {chapters.length > 0 && (
                  <div style={{ marginTop: '2rem' }}>
                    <EbookStructurePreview chapters={chapters} />
                  </div>
                )}
              </>
            )}
          </div>

          {/* Video Course Content Section - NEWLY ADDED */}
          {isVideo && (
            <div className="form-section">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 style={{ margin: 0 }}>Course Content</h2>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleAddModule}
                >
                  <FaPlus /> Add Module
                </button>
              </div>

              {modules.length > 0 ? (
                <DragDropContext onDragEnd={handleModuleDragEnd}>
                  <Droppable droppableId="modules">
                    {(provided) => (
                      <div {...provided.droppableProps} ref={provided.innerRef}>
                        {modules.map((module, index) => (
                          <Draggable key={module.id} draggableId={module.id} index={index}>
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <ModuleBuilder
                                  module={module}
                                  index={index}
                                  onUpdate={(updatedModule) => handleUpdateModule(module.id, updatedModule)}
                                  onDelete={() => handleDeleteModule(module.id)}
                                />
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              ) : (
                <div className="empty-state">
                  <p>No modules added yet. Click "Add Module" to begin structuring your course.</p>
                </div>
              )}

              {/* Course Structure Preview */}
              {modules.length > 0 && (
                <CourseStructurePreview modules={modules} />
              )}
            </div>
          )}

          <div className="form-section">
            <h2>Additional Details</h2>

            <div className="form-group">
              <label htmlFor="learningObjectives">Learning Objectives (one per line)</label>
              <textarea
                id="learningObjectives"
                name="learningObjectives"
                value={formData.learningObjectives}
                onChange={handleInputChange}
                placeholder="What will students learn?"
                rows={5}
              />
            </div>

            <div className="form-group">
              <label htmlFor="requirements">Requirements (one per line)</label>
              <textarea
                id="requirements"
                name="requirements"
                value={formData.requirements}
                onChange={handleInputChange}
                placeholder="What do students need to know beforehand?"
                rows={4}
              />
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate('/instructor/courses')}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Updating...' : isEbook ? 'Update Ebook' : 'Update Course'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditCourse;
